import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, FileText, Upload, X, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Maintenance = ({ user }) => {
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [formCategoryFilter, setFormCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [loadingPhotos, setLoadingPhotos] = useState({}); // Track which records are loading photos
  const [formData, setFormData] = useState({
    equipmentId: '',
    date: new Date().toISOString().split('T')[0],
    technician: user?.name || '',
    notes: '',
    gelReplacement: false,
    gelReplacementDate: '',
    capReplacement: false,
    capReplacementDate: '',
    phProbeReplacement: false,
    phProbeReplacementDate: '',
    chlorineProbeReplacement: false,
    chlorineProbeReplacementDate: '',
    filterBedReplacement: false,
    filterBedReplacementDate: '',
    stellarReplacement: false,
    stellarReplacementDate: '',
    sixWayValveReplacement: false,
    sixWayValveReplacementDate: '',
    replacementOther: false,
    replacementOtherDate: '',
    replacementOtherDescription: '',
    pumpRevision: false,
    pumpRevisionDate: '',
    pumpActivation: false,
    pumpActivationDate: '',
    pumpWorkOther: false,
    pumpWorkOtherDate: '',
    pumpWorkOtherDescription: '',
    fiberglassRestoration: false,
    fiberglassRestorationDate: '',
    edgeColoring: false,
    edgeColoringDate: '',
    skimmerColoring: false,
    skimmerColoringDate: '',
    skimmerCleaning: false,
    skimmerCleaningDate: '',
    tilePlacement: false,
    tilePlacementDate: '',
    startingBlockMaintenance: false,
    startingBlockMaintenanceDate: '',
    poolWorkOther: false,
    poolWorkOtherDate: '',
    poolWorkOtherDescription: ''
  });
  const [photos, setPhotos] = useState([]);

  const categories = [
    'Piscina 15m',
    'Piscina 20m',
    'Piscina 25m',
    'Piscina 50m',
    'Riscaldamento',
    'Sistema Sanitario',
    'Altro'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Filter records based on category and/or equipment selection
    let filtered = records;
    
    // Filter by category first
    if (selectedCategory) {
      const categoryEquipmentIds = equipment
        .filter(eq => eq.category === selectedCategory)
        .map(eq => eq.id);
      filtered = filtered.filter(r => categoryEquipmentIds.includes(r.equipmentId));
    }
    
    // Then filter by specific equipment if selected
    if (selectedEquipment) {
      filtered = filtered.filter(r => r.equipmentId === selectedEquipment);
    }
    
    setFilteredRecords(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [selectedCategory, selectedEquipment, records, equipment]);

  const fetchData = async () => {
    try {
      const [recordsRes, equipmentRes] = await Promise.all([
        axiosInstance.get('/maintenance'),
        axiosInstance.get('/equipment')
      ]);
      setRecords(recordsRes.data);
      setFilteredRecords(recordsRes.data);
      setEquipment(equipmentRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const loadRecordPhotos = async (recordId) => {
    setLoadingPhotos(prev => ({ ...prev, [recordId]: true }));
    
    try {
      const response = await axiosInstance.get(`/maintenance/${recordId}/photos`);
      
      // Update the record with photos
      setRecords(prevRecords => 
        prevRecords.map(r => 
          r.id === recordId ? { ...r, photos: response.data.photos } : r
        )
      );
      
      toast.success('Foto caricate!');
    } catch (error) {
      toast.error('Errore nel caricamento delle foto');
    } finally {
      setLoadingPhotos(prev => ({ ...prev, [recordId]: false }));
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axiosInstance.post('/maintenance/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.photo;
      });

      const uploadedPhotos = await Promise.all(uploadPromises);
      setPhotos([...photos, ...uploadedPhotos]);
      toast.success('Foto caricate!');
    } catch (error) {
      toast.error('Errore durante il caricamento delle foto');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, photos };
      
      if (editingRecord) {
        // Update existing record
        await axiosInstance.put(`/maintenance/${editingRecord.id}`, payload);
        toast.success('Manutenzione aggiornata!');
      } else {
        // Create new record
        await axiosInstance.post('/maintenance', payload);
        toast.success('Manutenzione registrata!');
      }
      
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const resetForm = () => {
    setFormData({
      equipmentId: '',
      date: new Date().toISOString().split('T')[0],
      technician: user?.name || '',
      notes: '',
      gelReplacement: false,
      gelReplacementDate: '',
      capReplacement: false,
      capReplacementDate: '',
      phProbeReplacement: false,
      phProbeReplacementDate: '',
      chlorineProbeReplacement: false,
      chlorineProbeReplacementDate: '',
      filterBedReplacement: false,
      filterBedReplacementDate: '',
      stellarReplacement: false,
      stellarReplacementDate: '',
      sixWayValveReplacement: false,
      sixWayValveReplacementDate: '',
      replacementOther: false,
      replacementOtherDate: '',
      replacementOtherDescription: '',
      pumpRevision: false,
      pumpRevisionDate: '',
      pumpActivation: false,
      pumpActivationDate: '',
      pumpWorkOther: false,
      pumpWorkOtherDate: '',
      pumpWorkOtherDescription: '',
      fiberglassRestoration: false,
      fiberglassRestorationDate: '',
      edgeColoring: false,
      edgeColoringDate: '',
      skimmerColoring: false,
      skimmerColoringDate: '',
      skimmerCleaning: false,
      skimmerCleaningDate: '',
      tilePlacement: false,
      tilePlacementDate: '',
      startingBlockMaintenance: false,
      startingBlockMaintenanceDate: '',
      poolWorkOther: false,
      poolWorkOtherDate: '',
      poolWorkOtherDescription: ''
    });
    setPhotos([]);
    setEditingRecord(null);
    setFormCategoryFilter('');
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setFormData({
      equipmentId: record.equipmentId,
      date: record.date,
      technician: record.technician,
      notes: record.notes,
      gelReplacement: record.gelReplacement || false,
      gelReplacementDate: record.gelReplacementDate || '',
      capReplacement: record.capReplacement || false,
      capReplacementDate: record.capReplacementDate || '',
      phProbeReplacement: record.phProbeReplacement || false,
      phProbeReplacementDate: record.phProbeReplacementDate || '',
      chlorineProbeReplacement: record.chlorineProbeReplacement || false,
      chlorineProbeReplacementDate: record.chlorineProbeReplacementDate || '',
      filterBedReplacement: record.filterBedReplacement || false,
      filterBedReplacementDate: record.filterBedReplacementDate || '',
      stellarReplacement: record.stellarReplacement || false,
      stellarReplacementDate: record.stellarReplacementDate || '',
      sixWayValveReplacement: record.sixWayValveReplacement || false,
      sixWayValveReplacementDate: record.sixWayValveReplacementDate || '',
      replacementOther: record.replacementOther || false,
      replacementOtherDate: record.replacementOtherDate || '',
      replacementOtherDescription: record.replacementOtherDescription || '',
      pumpRevision: record.pumpRevision || false,
      pumpRevisionDate: record.pumpRevisionDate || '',
      pumpActivation: record.pumpActivation || false,
      pumpActivationDate: record.pumpActivationDate || '',
      pumpWorkOther: record.pumpWorkOther || false,
      pumpWorkOtherDate: record.pumpWorkOtherDate || '',
      pumpWorkOtherDescription: record.pumpWorkOtherDescription || '',
      fiberglassRestoration: record.fiberglassRestoration || false,
      fiberglassRestorationDate: record.fiberglassRestorationDate || '',
      edgeColoring: record.edgeColoring || false,
      edgeColoringDate: record.edgeColoringDate || '',
      skimmerColoring: record.skimmerColoring || false,
      skimmerColoringDate: record.skimmerColoringDate || '',
      skimmerCleaning: record.skimmerCleaning || false,
      skimmerCleaningDate: record.skimmerCleaningDate || '',
      tilePlacement: record.tilePlacement || false,
      tilePlacementDate: record.tilePlacementDate || '',
      startingBlockMaintenance: record.startingBlockMaintenance || false,
      startingBlockMaintenanceDate: record.startingBlockMaintenanceDate || '',
      poolWorkOther: record.poolWorkOther || false,
      poolWorkOtherDate: record.poolWorkOtherDate || '',
      poolWorkOtherDescription: record.poolWorkOtherDescription || ''
    });
    setPhotos(record.photos || []);
    setDialogOpen(true);
  };

  const handleDeleteRecord = async (record) => {
    if (!window.confirm(`Sei sicuro di voler eliminare questa manutenzione?\n\nImpianto: ${record.equipmentName}\nData: ${new Date(record.date).toLocaleDateString('it-IT')}\nTecnico: ${record.technician}`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/maintenance/${record.id}`);
      toast.success('Manutenzione eliminata!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const canEdit = user?.role === 'admin' || user?.role === 'maintainer';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const typeColors = {
    preventive: 'badge-green',
    corrective: 'badge-yellow',
    inspection: 'badge-blue'
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="maintenance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Manutenzioni</h1>
          <p className="text-gray-600">Registro delle manutenzioni effettuate</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                data-testid="add-maintenance-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova Manutenzione
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="maintenance-dialog">
              <DialogHeader>
                <DialogTitle>{editingRecord ? 'Modifica Manutenzione' : 'Nuova Manutenzione'}</DialogTitle>
                <DialogDescription>
                  {editingRecord ? 'Modifica i dati della manutenzione' : 'Registra una nuova manutenzione'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Category Filter for Equipment Selection */}
                <div>
                  <Label htmlFor="formCategoryFilter">Categoria (opzionale)</Label>
                  <select
                    id="formCategoryFilter"
                    value={formCategoryFilter}
                    onChange={(e) => {
                      setFormCategoryFilter(e.target.value);
                      // Reset equipment selection when category changes
                      setFormData({ ...formData, equipmentId: '' });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="maintenance-category-filter"
                  >
                    <option value="">Tutte le categorie</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {formCategoryFilter && (
                    <p className="text-xs text-gray-500 mt-1">
                      Filtrando per: {formCategoryFilter}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="equipmentId">Impianto</Label>
                    <select
                      id="equipmentId"
                      value={formData.equipmentId}
                      onChange={(e) => setFormData({ ...formData, equipmentId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="maintenance-equipment-select"
                    >
                      <option value="">Seleziona impianto</option>
                      {equipment
                        .filter(item => !formCategoryFilter || item.category === formCategoryFilter)
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} {item.category && `(${item.category})`}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="date">Data</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="maintenance-date-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="technician">Tecnico</Label>
                  <Input
                    id="technician"
                    value={formData.technician}
                    onChange={(e) => setFormData({ ...formData, technician: e.target.value })}
                    required
                    data-testid="maintenance-technician-input"
                  />
                </div>
                
                {/* Sostituzioni Effettuate */}
                <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-900">Sostituzioni Effettuate</h4>
                  <div className="space-y-3">
                    {/* Sostituzione Gel */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.gelReplacement}
                          onChange={(e) => setFormData({ ...formData, gelReplacement: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Sostituzione gel</span>
                      </label>
                      {formData.gelReplacement && (
                        <Input
                          type="date"
                          value={formData.gelReplacementDate}
                          onChange={(e) => setFormData({ ...formData, gelReplacementDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Sostituzione Calotta */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.capReplacement}
                          onChange={(e) => setFormData({ ...formData, capReplacement: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Sostituzione calotta</span>
                      </label>
                      {formData.capReplacement && (
                        <Input
                          type="date"
                          value={formData.capReplacementDate}
                          onChange={(e) => setFormData({ ...formData, capReplacementDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Sostituzione Sonda pH */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.phProbeReplacement}
                          onChange={(e) => setFormData({ ...formData, phProbeReplacement: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Sostituzione sonda pH</span>
                      </label>
                      {formData.phProbeReplacement && (
                        <Input
                          type="date"
                          value={formData.phProbeReplacementDate}
                          onChange={(e) => setFormData({ ...formData, phProbeReplacementDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Sostituzione Sonda Cloro */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.chlorineProbeReplacement}
                          onChange={(e) => setFormData({ ...formData, chlorineProbeReplacement: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Sostituzione sonda cloro</span>
                      </label>
                      {formData.chlorineProbeReplacement && (
                        <Input
                          type="date"
                          value={formData.chlorineProbeReplacementDate}
                          onChange={(e) => setFormData({ ...formData, chlorineProbeReplacementDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Sostituzione Letto Filtrante */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.filterBedReplacement}
                          onChange={(e) => setFormData({ ...formData, filterBedReplacement: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Sostituzione letto filtrante</span>
                      </label>
                      {formData.filterBedReplacement && (
                        <Input
                          type="date"
                          value={formData.filterBedReplacementDate}
                          onChange={(e) => setFormData({ ...formData, filterBedReplacementDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Sostituzione Stellari */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.stellarReplacement}
                          onChange={(e) => setFormData({ ...formData, stellarReplacement: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Sostituzione stellari</span>
                      </label>
                      {formData.stellarReplacement && (
                        <Input
                          type="date"
                          value={formData.stellarReplacementDate}
                          onChange={(e) => setFormData({ ...formData, stellarReplacementDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Sostituzione Valvola 6 Vie */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.sixWayValveReplacement}
                          onChange={(e) => setFormData({ ...formData, sixWayValveReplacement: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Sostituzione valvola 6 vie</span>
                      </label>
                      {formData.sixWayValveReplacement && (
                        <Input
                          type="date"
                          value={formData.sixWayValveReplacementDate}
                          onChange={(e) => setFormData({ ...formData, sixWayValveReplacementDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Altro - Sostituzioni */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center space-x-2 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={formData.replacementOther}
                            onChange={(e) => setFormData({ ...formData, replacementOther: e.target.checked })}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-900">Altro</span>
                        </label>
                        {formData.replacementOther && (
                          <Input
                            type="date"
                            value={formData.replacementOtherDate}
                            onChange={(e) => setFormData({ ...formData, replacementOtherDate: e.target.value })}
                            className="w-40"
                            placeholder="Data"
                          />
                        )}
                      </div>
                      {formData.replacementOther && (
                        <Input
                          type="text"
                          value={formData.replacementOtherDescription}
                          onChange={(e) => setFormData({ ...formData, replacementOtherDescription: e.target.value })}
                          className="w-full"
                          placeholder="Descrivi la sostituzione effettuata..."
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Operazioni Pompa */}
                <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-900">Operazioni Pompa</h4>
                  <div className="space-y-3">
                    {/* Revisione Pompa */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.pumpRevision}
                          onChange={(e) => setFormData({ ...formData, pumpRevision: e.target.checked })}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Revisione pompa</span>
                      </label>
                      {formData.pumpRevision && (
                        <Input
                          type="date"
                          value={formData.pumpRevisionDate}
                          onChange={(e) => setFormData({ ...formData, pumpRevisionDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Accensione Pompa */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.pumpActivation}
                          onChange={(e) => setFormData({ ...formData, pumpActivation: e.target.checked })}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Accensione pompa</span>
                      </label>
                      {formData.pumpActivation && (
                        <Input
                          type="date"
                          value={formData.pumpActivationDate}
                          onChange={(e) => setFormData({ ...formData, pumpActivationDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Altro - Operazioni Pompa */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center space-x-2 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={formData.pumpWorkOther}
                            onChange={(e) => setFormData({ ...formData, pumpWorkOther: e.target.checked })}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-gray-900">Altro</span>
                        </label>
                        {formData.pumpWorkOther && (
                          <Input
                            type="date"
                            value={formData.pumpWorkOtherDate}
                            onChange={(e) => setFormData({ ...formData, pumpWorkOtherDate: e.target.value })}
                            className="w-40"
                            placeholder="Data"
                          />
                        )}
                      </div>
                      {formData.pumpWorkOther && (
                        <Input
                          type="text"
                          value={formData.pumpWorkOtherDescription}
                          onChange={(e) => setFormData({ ...formData, pumpWorkOtherDescription: e.target.value })}
                          className="w-full"
                          placeholder="Descrivi l'operazione effettuata..."
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Lavori in vasca */}
                <div className="bg-green-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-900">Lavori in vasca</h4>
                  <div className="space-y-3">
                    {/* Ripristino Vetroresina */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.fiberglassRestoration}
                          onChange={(e) => setFormData({ ...formData, fiberglassRestoration: e.target.checked })}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Ripristino vetroresina</span>
                      </label>
                      {formData.fiberglassRestoration && (
                        <Input
                          type="date"
                          value={formData.fiberglassRestorationDate}
                          onChange={(e) => setFormData({ ...formData, fiberglassRestorationDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Colorazione bordi vetroresina */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.edgeColoring}
                          onChange={(e) => setFormData({ ...formData, edgeColoring: e.target.checked })}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Colorazione bordi vetroresina</span>
                      </label>
                      {formData.edgeColoring && (
                        <Input
                          type="date"
                          value={formData.edgeColoringDate}
                          onChange={(e) => setFormData({ ...formData, edgeColoringDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Colorazione skimmer */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.skimmerColoring}
                          onChange={(e) => setFormData({ ...formData, skimmerColoring: e.target.checked })}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Colorazione skimmer</span>
                      </label>
                      {formData.skimmerColoring && (
                        <Input
                          type="date"
                          value={formData.skimmerColoringDate}
                          onChange={(e) => setFormData({ ...formData, skimmerColoringDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Pulizia skimmer */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.skimmerCleaning}
                          onChange={(e) => setFormData({ ...formData, skimmerCleaning: e.target.checked })}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Pulizia skimmer</span>
                      </label>
                      {formData.skimmerCleaning && (
                        <Input
                          type="date"
                          value={formData.skimmerCleaningDate}
                          onChange={(e) => setFormData({ ...formData, skimmerCleaningDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Sostituzione piastrelle */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.tilePlacement}
                          onChange={(e) => setFormData({ ...formData, tilePlacement: e.target.checked })}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Sostituzione piastrelle</span>
                      </label>
                      {formData.tilePlacement && (
                        <Input
                          type="date"
                          value={formData.tilePlacementDate}
                          onChange={(e) => setFormData({ ...formData, tilePlacementDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Manutenzioni blocchi partenza */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={formData.startingBlockMaintenance}
                          onChange={(e) => setFormData({ ...formData, startingBlockMaintenance: e.target.checked })}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Manutenzioni blocchi partenza</span>
                      </label>
                      {formData.startingBlockMaintenance && (
                        <Input
                          type="date"
                          value={formData.startingBlockMaintenanceDate}
                          onChange={(e) => setFormData({ ...formData, startingBlockMaintenanceDate: e.target.value })}
                          className="w-40"
                          placeholder="Data"
                        />
                      )}
                    </div>

                    {/* Altro */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center space-x-2 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={formData.poolWorkOther}
                            onChange={(e) => setFormData({ ...formData, poolWorkOther: e.target.checked })}
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                          />
                          <span className="text-sm font-medium text-gray-900">Altro</span>
                        </label>
                        {formData.poolWorkOther && (
                          <Input
                            type="date"
                            value={formData.poolWorkOtherDate}
                            onChange={(e) => setFormData({ ...formData, poolWorkOtherDate: e.target.value })}
                            className="w-40"
                            placeholder="Data"
                          />
                        )}
                      </div>
                      {formData.poolWorkOther && (
                        <Input
                          type="text"
                          value={formData.poolWorkOtherDescription}
                          onChange={(e) => setFormData({ ...formData, poolWorkOtherDescription: e.target.value })}
                          className="w-full"
                          placeholder="Descrivi il lavoro effettuato..."
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Note (opzionale)</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                    placeholder="Aggiungi note o osservazioni..."
                    data-testid="maintenance-notes-textarea"
                  />
                </div>
                <div>
                  <Label>Foto</Label>
                  <div className="mt-2">
                    <label className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                      <Upload className="w-5 h-5 mr-2 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {uploading ? 'Caricamento...' : 'Carica foto'}
                      </span>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        disabled={uploading}
                        data-testid="maintenance-photo-input"
                      />
                    </label>
                  </div>
                  {photos.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {photos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={photo}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-20 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => setPhotos(photos.filter((_, i) => i !== index))}
                            className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  data-testid="maintenance-submit-button"
                >
                  {editingRecord ? 'Aggiorna Manutenzione' : 'Registra Manutenzione'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Category and Equipment Filters */}
      <Card className="shadow-md border-0">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category Filter */}
            <div>
              <Label htmlFor="categoryFilter" className="text-sm font-medium mb-2 block">
                Filtra per Categoria:
              </Label>
              <select
                id="categoryFilter"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedEquipment(''); // Reset equipment filter when category changes
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="category-filter-select"
              >
                <option value="">Tutte le categorie ({records.length} manutenzioni)</option>
                {categories.map((category) => {
                  const categoryEquipment = equipment.filter(eq => eq.category === category);
                  const count = records.filter(r => 
                    categoryEquipment.some(eq => eq.id === r.equipmentId)
                  ).length;
                  return (
                    <option key={category} value={category}>
                      {category} ({count} manutenzioni)
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Equipment Filter */}
            <div>
              <Label htmlFor="equipmentFilter" className="text-sm font-medium mb-2 block">
                {selectedCategory ? 'Filtra per Impianto Specifico:' : 'Filtra per Impianto:'}
              </Label>
              <select
                id="equipmentFilter"
                value={selectedEquipment}
                onChange={(e) => setSelectedEquipment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="equipment-filter-select"
              >
                <option value="">
                  {selectedCategory ? `Tutti gli impianti in ${selectedCategory}` : 'Tutti gli impianti'}
                </option>
                {equipment
                  .filter(item => !selectedCategory || item.category === selectedCategory)
                  .map((item) => {
                    const count = records.filter(r => r.equipmentId === item.id).length;
                    return (
                      <option key={item.id} value={item.id}>
                        {item.name} ({count} manutenzioni)
                      </option>
                    );
                  })}
              </select>
            </div>
          </div>

          {(selectedCategory || selectedEquipment) && (
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedCategory('');
                  setSelectedEquipment('');
                }}
                data-testid="clear-filters-button"
              >
                Pulisci filtri
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {filteredRecords.length === 0 ? (
          <Card className="shadow-md border-0">
            <CardContent className="pt-6 text-center text-gray-500">
              {selectedCategory && selectedEquipment
                ? `Nessuna manutenzione trovata per ${selectedEquipment}` 
                : selectedCategory 
                ? `Nessuna manutenzione trovata per la categoria ${selectedCategory}`
                : selectedEquipment
                ? 'Nessuna manutenzione trovata per questo impianto'
                : 'Nessuna manutenzione registrata'}
            </CardContent>
          </Card>
        ) : (
          <>
          {/* Paginazione */}
          {filteredRecords.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage).map((record) => (
          <Card key={record.id} className="card-hover shadow-lg border-0" data-testid={`maintenance-record-${record.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center flex-1">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 mr-3">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{record.equipmentName}</CardTitle>
                    <div className="flex gap-2 mt-1 items-center">
                      <span className="text-sm text-gray-500">
                        {new Date(record.date).toLocaleDateString('it-IT')}
                      </span>
                      <span className="text-sm text-gray-400">•</span>
                      <span className="text-sm text-gray-600">{record.technician}</span>
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-2 ml-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditRecord(record)}
                      data-testid={`edit-maintenance-${record.id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Modifica
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteRecord(record)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-maintenance-${record.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Elimina
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {record.notes && <p className="text-sm text-gray-700 mb-3">{record.notes}</p>}
              
              {/* Sostituzioni Effettuate */}
              {(record.gelReplacement || record.capReplacement || record.phProbeReplacement || 
                record.chlorineProbeReplacement || record.filterBedReplacement || record.stellarReplacement || 
                record.sixWayValveReplacement || record.replacementOther) && (
                <div className="bg-blue-50 p-3 rounded-lg mb-3">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Sostituzioni effettuate:</h5>
                  <div className="flex flex-wrap gap-2">
                    {record.gelReplacement && (
                      <span className="badge badge-green">✓ Gel{record.gelReplacementDate && ` - ${new Date(record.gelReplacementDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.capReplacement && (
                      <span className="badge badge-green">✓ Calotta{record.capReplacementDate && ` - ${new Date(record.capReplacementDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.phProbeReplacement && (
                      <span className="badge badge-green">✓ Sonda pH{record.phProbeReplacementDate && ` - ${new Date(record.phProbeReplacementDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.chlorineProbeReplacement && (
                      <span className="badge badge-green">✓ Sonda cloro{record.chlorineProbeReplacementDate && ` - ${new Date(record.chlorineProbeReplacementDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.filterBedReplacement && (
                      <span className="badge badge-green">✓ Letto filtrante{record.filterBedReplacementDate && ` - ${new Date(record.filterBedReplacementDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.stellarReplacement && (
                      <span className="badge badge-green">✓ Stellari{record.stellarReplacementDate && ` - ${new Date(record.stellarReplacementDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.sixWayValveReplacement && (
                      <span className="badge badge-green">✓ Valvola 6 vie{record.sixWayValveReplacementDate && ` - ${new Date(record.sixWayValveReplacementDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.replacementOther && (
                      <span className="badge badge-green">✓ Altro: {record.replacementOtherDescription || 'N/A'}{record.replacementOtherDate && ` - ${new Date(record.replacementOtherDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Operazioni Pompa */}
              {(record.pumpRevision || record.pumpActivation || record.pumpWorkOther) && (
                <div className="bg-purple-50 p-3 rounded-lg mb-3">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Operazioni pompa:</h5>
                  <div className="flex flex-wrap gap-2">
                    {record.pumpRevision && (
                      <span className="badge badge-purple">✓ Revisione{record.pumpRevisionDate && ` - ${new Date(record.pumpRevisionDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.pumpActivation && (
                      <span className="badge badge-purple">✓ Accensione{record.pumpActivationDate && ` - ${new Date(record.pumpActivationDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.pumpWorkOther && (
                      <span className="badge badge-purple">✓ Altro: {record.pumpWorkOtherDescription || 'N/A'}{record.pumpWorkOtherDate && ` - ${new Date(record.pumpWorkOtherDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Lavori in Vasca */}
              {(record.fiberglassRestoration || record.edgeColoring || record.skimmerColoring || 
                record.skimmerCleaning || record.tilePlacement || record.startingBlockMaintenance || 
                record.poolWorkOther) && (
                <div className="bg-green-50 p-3 rounded-lg mb-3">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Lavori in vasca:</h5>
                  <div className="flex flex-wrap gap-2">
                    {record.fiberglassRestoration && (
                      <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>✓ Ripristino vetroresina{record.fiberglassRestorationDate && ` - ${new Date(record.fiberglassRestorationDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.edgeColoring && (
                      <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>✓ Colorazione bordi{record.edgeColoringDate && ` - ${new Date(record.edgeColoringDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.skimmerColoring && (
                      <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>✓ Colorazione skimmer{record.skimmerColoringDate && ` - ${new Date(record.skimmerColoringDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.skimmerCleaning && (
                      <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>✓ Pulizia skimmer{record.skimmerCleaningDate && ` - ${new Date(record.skimmerCleaningDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.tilePlacement && (
                      <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>✓ Sostituzione piastrelle{record.tilePlacementDate && ` - ${new Date(record.tilePlacementDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.startingBlockMaintenance && (
                      <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>✓ Blocchi partenza{record.startingBlockMaintenanceDate && ` - ${new Date(record.startingBlockMaintenanceDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                    {record.poolWorkOther && (
                      <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>✓ Altro: {record.poolWorkOtherDescription || 'N/A'}{record.poolWorkOtherDate && ` - ${new Date(record.poolWorkOtherDate).toLocaleDateString('it-IT')}`}</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Photos Section - Load on demand */}
              {record.photoCount > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-medium text-gray-900 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Foto ({record.photoCount})
                    </h5>
                    {(!record.photos || record.photos.length === 0) && !loadingPhotos[record.id] && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => loadRecordPhotos(record.id)}
                      >
                        Mostra foto
                      </Button>
                    )}
                    {loadingPhotos[record.id] && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled
                      >
                        Caricamento...
                      </Button>
                    )}
                    {record.photos && record.photos.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setRecords(prevRecords => 
                            prevRecords.map(r => 
                              r.id === record.id ? { ...r, photos: [] } : r
                            )
                          );
                        }}
                      >
                        Nascondi foto
                      </Button>
                    )}
                  </div>
                  
                  {record.photos && record.photos.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {record.photos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={photo}
                            alt={`Foto ${index + 1}`}
                            loading="lazy"
                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-all shadow-sm hover:shadow-md"
                            onClick={() => window.open(photo, '_blank')}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                            <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">Clicca per ingrandire</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        
        {/* Controlli paginazione */}
        {filteredRecords.length > recordsPerPage && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Precedente
            </Button>
            <span className="text-sm text-gray-600">
              Pagina {currentPage} di {Math.ceil(filteredRecords.length / recordsPerPage)}
            </span>
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredRecords.length / recordsPerPage), prev + 1))}
              disabled={currentPage === Math.ceil(filteredRecords.length / recordsPerPage)}
            >
              Successiva
            </Button>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default Maintenance;